// src/components/auth/SignIn.js
import { getCurrentUser, signIn } from "aws-amplify/auth";
import { useEffect, useRef, useState } from "react";

export default function SignIn({
  onSuccess,
  onRegister,
  onForgotPassword,
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    const container = document.querySelector(
      ".greenone-sign-in"
    );

    if (!container) {
      return undefined;
    }

    function styleSdxButton(button) {
      const shadowRoot = button.shadowRoot;

      if (
        !shadowRoot ||
        shadowRoot.querySelector(
          "[data-greenone-login-button-styles]"
        )
      ) {
        return;
      }

      const style = document.createElement("style");

      style.setAttribute(
        "data-greenone-login-button-styles",
        "true"
      );

      style.textContent = `
        button,
        button.primary {
          background: #005654 !important;
          border-color: #005654 !important;
          color: #fff !important;
        }

        button:hover,
        button.primary:hover {
          background: #026F6D !important;
          border-color: #026F6D !important;
          color: #fff !important;
        }

        button:focus-visible,
        button.primary:focus-visible {
          outline: 3px solid #005654 !important;
          outline-offset: 2px !important;
        }
      `;

      shadowRoot.appendChild(style);
    }

    function styleSdxInput(input) {
      const shadowRoot = input.shadowRoot;

      if (
        !shadowRoot ||
        shadowRoot.querySelector(
          "[data-greenone-login-input-styles]"
        )
      ) {
        return;
      }

      const style = document.createElement("style");

      style.setAttribute(
        "data-greenone-login-input-styles",
        "true"
      );

      style.textContent = `
        input:focus,
        input:focus-visible {
          border-color: #026F6D !important;
          outline-color: #026F6D !important;
          box-shadow: 0 0 0 1px #026F6D !important;
        }

        :host(:focus-within) input {
          border-color: #026F6D !important;
          outline-color: #026F6D !important;
          box-shadow: 0 0 0 1px #026F6D !important;
        }

        input::-ms-reveal,
          input::-ms-clear {
          display: none !important;
    }
      `;

      shadowRoot.appendChild(style);
    }

    function applyComponentStyles() {
      container
        .querySelectorAll("sdx-button")
        .forEach(styleSdxButton);

      container
        .querySelectorAll("sdx-input")
        .forEach(styleSdxInput);
    }

    applyComponentStyles();

    const observer = new MutationObserver(
      applyComponentStyles
    );

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (passwordRef.current) {
      passwordRef.current.type = showPassword ? "text" : "password";
    }
  }, [showPassword]);

  useEffect(() => {
  const el = passwordRef.current;
  if (!el) return undefined;

  const handleInput = (e) => {
    setPassword(e.target.value);
  };

  el.addEventListener("input", handleInput);

  return () => {
    el.removeEventListener("input", handleInput);
  };
}, []);

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      handleLogin(e);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const usernameValue =
      usernameRef.current?.value || username;

    const passwordValue =
      passwordRef.current?.value || password;


    try {
      await signIn({ username: usernameValue, password: passwordValue });
      const user = await getCurrentUser();
      onSuccess(user);
    } catch (err) {
      console.error("Anmeldung fehlgeschlagen:", err);
      setError(
        "Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="sdx greenone-sign-in"
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Hintergrundbild mit Blur */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url('/images/Garage_2.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(8px) brightness(0.75)",
          transform: "scale(1.05)",
        }}
      ></div>

      {/* Login-Box */}
      <div
        style={{
          position: "relative",
          background: "rgba(255, 255, 255, 0.97)",
          borderRadius: "16px",
          padding: "2rem",
          width: "100%",
          maxWidth: "400px",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
          border: "1px solid #b7cfcc",
          boxSizing: "border-box",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            marginTop: 0,
            marginBottom: "0.5rem",
            color: "#004A53",
          }}
        >
          GreenOne Anmeldung
        </h2>

        <p
          style={{
            textAlign: "center",
            marginBottom: "1.5rem",
            color: "#64748b",
          }}
        >
          Melden Sie sich mit Ihrem GreenOne-Konto an.
        </p>

        <form
          onSubmit={handleLogin}
          style={{
            display: "grid",
            gap: "1rem",
          }}
        >
          <sdx-input
            label="E-Mail"
            ref={usernameRef}
            placeholder="E-Mail eingeben"
            value={username}
            //onInput={(e) => setUsername(e.target.value)}
            onKeyDown={handleEnter}
            clarity-mask="True"
            required
          ></sdx-input>

          <div style={{ position: "relative" }}>
            <sdx-input
              label="Passwort"
              ref={passwordRef}
              type="password"
              placeholder="Passwort eingeben"
              value={password}
              onKeyDown={handleEnter}
              clarity-mask="True"
              required
            ></sdx-input>

          {password && (
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
              style={{
                position: "absolute",
                right: "0.8rem",
                top: "2.6rem",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                color: "#64748b",
              }}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </button>
          )}
          </div>



          {error && (
            <p
              style={{
                color: "#dc2626",
                fontSize: "0.9rem",
              }}
            >
              {error}
            </p>
          )}

          <sdx-button
            type="submit"
            variant="primary"
            label={loading ? "Anmeldung läuft..." : "Anmelden"}
            disabled={loading}
          ></sdx-button>

          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <sdx-loading-spinner></sdx-loading-spinner>
            </div>
          )}
        </form>

        <div
          style={{
            marginTop: "1.5rem",
            paddingTop: "1.25rem",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.9rem",
          }}
        >
          <span style={{ display: "inline-block" }}>
            <a
              onClick={onRegister}
              style={{
                color: "#026F6D",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Jetzt registrieren
            </a>
          </span>

          <span style={{ display: "inline-block" }}>
            <a
              onClick={onForgotPassword}
              style={{
                color: "#026F6D",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Passwort vergessen?
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
